// src/components/dashboard/Workshop3Dashboard.js
// Professional Workshop 3 Dashboard using Tremor components (Light Theme)
import React from 'react';
import {
    Card,
    Title,
    Text,
    Metric,
    Grid,
    DonutChart,
    BarList,
    Flex,
    Legend,
    Badge,
    BadgeDelta,
    ProgressBar
} from "@tremor/react";
import { useTranslation } from 'react-i18next';
import {
    Users,
    Shield,
    AlertTriangle,
    Globe,
    Building,
    Eye,
    CheckCircle,
    XCircle
} from 'lucide-react';

const Workshop3Dashboard = ({
    stakeholdersData = [],
    stakeholdersLoading = false,
    threatZoneDistribution = [],
    stakeholderCategoriesData = [],
    cyberMaturityDistribution = []
}) => {
    const { t } = useTranslation();

    // Calculate totals
    const totalStakeholders = stakeholdersData.length;
    const internalCount = stakeholdersData.filter(s => s.type === 'internal').length;
    const externalCount = stakeholdersData.filter(s => s.type === 'external').length;
    const retainedCount = stakeholdersData.filter(s => s.retained === true).length;
    const notRetainedCount = stakeholdersData.filter(s => s.retained === false).length;

    // Count danger zone stakeholders
    const dangerZoneCount = threatZoneDistribution.find(z => z.zone === 'danger')?.count || 0;

    // Process threat zone data for chart
    const threatZoneChartData = threatZoneDistribution.map(zone => {
        const zoneLabels = {
            danger: t('dashboard.workshop3.dangerZone'),
            control: t('dashboard.workshop3.controlZone'),
            watch: t('dashboard.workshop3.watchZone'),
            outside: t('dashboard.workshop3.outsideZone')
        };
        return {
            name: zoneLabels[zone.zone] || zone.zone,
            value: zone.count || 0
        };
    }).filter(item => item.value > 0);

    // Process categories for chart
    const categoryChartData = stakeholderCategoriesData.map(cat => ({
        name: cat.name,
        value: cat.value
    })).filter(item => item.value > 0);

    // Process maturity distribution for bar list
    const maturityChartData = cyberMaturityDistribution.map(item => ({
        name: item.level,
        value: item.count
    })).filter(item => item.value > 0);

    // Color arrays
    const zoneColors = ["rose", "amber", "teal", "gray"];
    const categoryColors = ["blue", "emerald", "amber", "rose", "violet", "cyan"];
    const retentionColors = ["emerald", "gray"];
    const typeColors = ["blue", "violet"];

    // Process type distribution
    const typeChartData = [
        { name: t('dashboard.workshop3.internal'), value: internalCount },
        { name: t('dashboard.workshop3.external'), value: externalCount }
    ].filter(item => item.value > 0);

    // Process retention data
    const retentionChartData = [
        { name: t('dashboard.workshop3.retained'), value: retainedCount },
        { name: t('dashboard.workshop3.notRetained'), value: notRetainedCount }
    ].filter(item => item.value > 0);

    if (stakeholdersLoading) {
        return (
            <Card className="!bg-white">
                <Flex justifyContent="center" alignItems="center" className="h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent mr-3"></div>
                    <Text>{t('dashboard.status.loading')}</Text>
                </Flex>
            </Card>
        );
    }

    return (
        <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
            {/* KPI Cards Row */}
            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
                {/* Stakeholders Card */}
                <Card decoration="top" decorationColor="teal" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop3.stakeholders')}</Text>
                            <Metric className="!text-gray-900">{totalStakeholders}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {internalCount} {t('dashboard.workshop3.internal').toLowerCase()} / {externalCount} {t('dashboard.workshop3.external').toLowerCase()}
                            </Text>
                        </div>
                        <div className="p-2 bg-teal-100 rounded-lg">
                            <Users className="w-6 h-6 text-teal-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Danger Zone Card */}
                <Card decoration="top" decorationColor="rose" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop3.dangerZone')}</Text>
                            <Metric className="!text-gray-900">{dangerZoneCount}</Metric>
                            {dangerZoneCount > 0 && (
                                <BadgeDelta deltaType="increase" size="xs" className="mt-1">
                                    {t('dashboard.workshop3.highRisk')}
                                </BadgeDelta>
                            )}
                        </div>
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-rose-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Retained Card */}
                <Card decoration="top" decorationColor="emerald" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop3.retained')}</Text>
                            <Metric className="!text-gray-900">{retainedCount}</Metric>
                            <Flex className="mt-1" justifyContent="start" alignItems="center">
                                <Badge color="emerald" size="xs">
                                    {totalStakeholders > 0 ? Math.round((retainedCount / totalStakeholders) * 100) : 0}% {t('dashboard.workshop3.ofTotal')}
                                </Badge>
                            </Flex>
                        </div>
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Categories Card */}
                <Card decoration="top" decorationColor="cyan" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.workshop3.categories')}</Text>
                            <Metric className="!text-gray-900">{stakeholderCategoriesData.length}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {t('dashboard.workshop3.typesIdentified')}
                            </Text>
                        </div>
                        <div className="p-2 bg-cyan-100 rounded-lg">
                            <Globe className="w-6 h-6 text-cyan-600" />
                        </div>
                    </Flex>
                </Card>
            </Grid>

            {/* Charts Grid */}
            <Grid numItems={1} numItemsLg={2} className="gap-6">
                {/* Threat Zone Distribution - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop3.threatZoneDistribution')}</Title>
                    {threatZoneChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={threatZoneChartData}
                                category="value"
                                index="name"
                                colors={zoneColors}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={threatZoneChartData.map(d => d.name)}
                                colors={zoneColors}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                        </div>
                    )}
                </Card>

                {/* Stakeholder Categories - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop3.stakeholderCategories')}</Title>
                    {categoryChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={categoryChartData}
                                category="value"
                                index="name"
                                colors={categoryColors}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={categoryChartData.map(d => d.name)}
                                colors={categoryColors}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                        </div>
                    )}
                </Card>

                {/* Internal/External Distribution - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop3.typeDistribution')}</Title>
                    {typeChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={typeChartData}
                                category="value"
                                index="name"
                                colors={typeColors}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={typeChartData.map(d => d.name)}
                                colors={typeColors}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop3.noStakeholders')}</Text>
                        </div>
                    )}
                </Card>

                {/* Cyber Maturity - Bar List */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop3.cyberMaturity')}</Title>
                    {maturityChartData.length > 0 ? (
                        <BarList
                            data={maturityChartData}
                            className="mt-4"
                            showAnimation={true}
                            color="teal"
                        />
                    ) : (
                        <div className="h-40 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.workshop3.noMaturityData')}</Text>
                        </div>
                    )}
                </Card>
            </Grid>

            {/* Retention Summary */}
            {totalStakeholders > 0 && (
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop3.retentionSummary')}</Title>
                    <Grid numItems={1} numItemsMd={2} className="gap-4 mt-4">
                        <Card decoration="left" decorationColor="emerald" className="!bg-white">
                            <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                <CheckCircle className="w-5 h-5 text-emerald-500" />
                                <Text className="!text-gray-700">{t('dashboard.workshop3.retained')}</Text>
                            </Flex>
                            <Metric className="mt-2 !text-gray-900">{retainedCount}</Metric>
                            <ProgressBar
                                value={totalStakeholders > 0 ? (retainedCount / totalStakeholders) * 100 : 0}
                                color="emerald"
                                className="mt-2"
                            />
                            <Text className="text-xs !text-gray-500 mt-1">
                                {totalStakeholders > 0 ? Math.round((retainedCount / totalStakeholders) * 100) : 0}% {t('dashboard.workshop3.ofTotal')}
                            </Text>
                        </Card>
                        <Card decoration="left" decorationColor="gray" className="!bg-white">
                            <Flex alignItems="center" justifyContent="start" className="space-x-2">
                                <XCircle className="w-5 h-5 text-gray-500" />
                                <Text className="!text-gray-700">{t('dashboard.workshop3.notRetained')}</Text>
                            </Flex>
                            <Metric className="mt-2 !text-gray-900">{notRetainedCount}</Metric>
                            <ProgressBar
                                value={totalStakeholders > 0 ? (notRetainedCount / totalStakeholders) * 100 : 0}
                                color="gray"
                                className="mt-2"
                            />
                            <Text className="text-xs !text-gray-500 mt-1">
                                {totalStakeholders > 0 ? Math.round((notRetainedCount / totalStakeholders) * 100) : 0}% {t('dashboard.workshop3.ofTotal')}
                            </Text>
                        </Card>
                    </Grid>
                </Card>
            )}

            {/* Empty State */}
            {totalStakeholders === 0 && (
                <Card className="!bg-white">
                    <Flex justifyContent="center" alignItems="center" className="h-40 flex-col">
                        <Users className="w-12 h-12 text-gray-300 mb-3" />
                        <Text className="!text-gray-500">{t('dashboard.workshop3.noStakeholders')}</Text>
                        <Text className="text-xs !text-gray-400 mt-1">{t('dashboard.workshop3.completeWorkshop3')}</Text>
                    </Flex>
                </Card>
            )}
        </div>
    );
};

export default Workshop3Dashboard;
